import { useRef } from 'react';
import ImageStage from '@/components/ui/image-stage';
import DownloadIcon from '../../../asset/icons/photo-viewer/download.svg?react';
import RequestSupportIcon from '../../../asset/icons/photo-viewer/request-support.svg?react';
import CloseIcon from '../../../asset/icons/photo-viewer/close.svg?react';

/** Horizontal travel (px) that counts as a swipe rather than a tap. */
const SWIPE_THRESHOLD_PX = 48;

interface PhoneAssetViewerProps {
  photoName: string;
  currentIndex: number;
  totalImages: number;
  src: string;
  srcSet?: string;
  placeholderSrc?: string;
  loaded: boolean;
  onLoaded: (src: string) => void;
  isDisabled: boolean;
  /** Hidden for staged photos (same rule as the desktop toolbar). */
  showRequestSupport: boolean;
  onRequestSupport: () => void;
  onDownload: () => void;
  onClose: () => void;
  onPrev: () => void;
  onNext: () => void;
}

/**
 * The max-sm asset viewer presentation (Unit 4 spec §1.11, design 2026-08-19
 * round 4): a fullscreen black takeover — close top-left, "n / N" centered,
 * Request Support + Download top-right, caption along the bottom, and swipe
 * navigation in place of the desktop's overlay arrows. The thumbnail strip
 * and services badge carousel deliberately don't render here (ordering on a
 * phone goes through the selection bar's Services sheet); at sm+ the classic
 * windowed viewer is unchanged. Presentational only — the host owns all
 * viewer state.
 */
export default function PhoneAssetViewer({
  photoName,
  currentIndex,
  totalImages,
  src,
  srcSet,
  placeholderSrc,
  loaded,
  onLoaded,
  isDisabled,
  showRequestSupport,
  onRequestSupport,
  onDownload,
  onClose,
  onPrev,
  onNext
}: PhoneAssetViewerProps) {
  const touchStart = useRef<{ x: number; y: number } | null>(null);

  const handleTouchStart = (e: React.TouchEvent) => {
    const t = e.touches[0];
    touchStart.current = { x: t.clientX, y: t.clientY };
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    const start = touchStart.current;
    touchStart.current = null;
    if (!start || isDisabled) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - start.x;
    const dy = t.clientY - start.y;
    // Dominant-axis check keeps a vertical scroll/dismiss gesture from also
    // paging the photo.
    if (Math.abs(dx) < SWIPE_THRESHOLD_PX || Math.abs(dx) <= Math.abs(dy)) {
      return;
    }
    if (dx < 0) onNext();
    else onPrev();
  };

  const iconButtonClass =
    'inline-flex h-11 w-11 items-center justify-center rounded-full text-white transition-colors hover:bg-white/20 disabled:opacity-50 disabled:cursor-not-allowed';

  return (
    <div className="flex h-full flex-col" aria-live="polite">
      {/* Header: safe-area padded so iOS browser chrome never overlaps the
          close target. The count is absolutely centered against the full row
          width so it doesn't drift when Request Support is hidden. */}
      <div className="relative flex shrink-0 items-center justify-between px-2 pt-[max(8px,env(safe-area-inset-top))] pb-1">
        <button
          type="button"
          aria-label="Close"
          className={iconButtonClass}
          onClick={onClose}
          disabled={isDisabled}
        >
          <CloseIcon className="h-6 w-6" />
        </button>
        <span
          data-testid="phone-viewer-count"
          className="pointer-events-none absolute inset-x-0 text-center text-sm font-semibold text-white/85"
        >
          {currentIndex + 1} / {totalImages}
        </span>
        <div className="z-10 flex items-center">
          {showRequestSupport && (
            <button
              type="button"
              aria-label="Request support"
              className={iconButtonClass}
              onClick={onRequestSupport}
              disabled={isDisabled}
            >
              <RequestSupportIcon className="h-6 w-6" />
            </button>
          )}
          <button
            type="button"
            aria-label="Download"
            className={iconButtonClass}
            onClick={onDownload}
            disabled={isDisabled}
          >
            <DownloadIcon className="h-6 w-6" />
          </button>
        </div>
      </div>

      <div
        data-testid="phone-viewer-stage"
        className="relative min-h-0 flex-1"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <ImageStage
          src={src}
          srcSet={srcSet}
          sizes="100vw"
          alt={photoName || `image-${currentIndex}`}
          placeholderSrc={placeholderSrc}
          loaded={loaded}
          onLoaded={onLoaded}
          className="h-full w-full bg-black"
          spinnerClassName="text-white/85"
        />
      </div>

      <div className="shrink-0 px-4 pt-3 pb-[max(20px,env(safe-area-inset-bottom))]">
        <p className="truncate text-[17px] font-semibold leading-6 text-white">
          {photoName}
        </p>
      </div>
    </div>
  );
}
